// Business types and product field groups. A business type only picks which
// groups of product fields the app shows; every business has every column.
// Group fields stored in products.attributes are display-only: business rules
// never read them.
import type { JsonValue, TenantSettingsRow } from '@ims/data'

export type GroupId =
  | 'brand'
  | 'pricing'
  | 'packs'
  | 'shelf'
  | 'trade'
  | 'batches'
  | 'variants'
  | 'serials'
  | 'wholesale'
  | 'scale'
  | 'pharmacy'
  | 'agro'
  | 'apparel'
  | 'electronics'
  | 'photo'
  | 'custom'

export type AttributeType = 'text' | 'number' | 'date' | 'yesno' | 'select'

/** A field kept in products.attributes (group fields and custom fields). */
export interface AttributeField {
  key: string
  label: string
  type: AttributeType
  options?: string[]
  placeholder?: string
}

export interface FieldGroup {
  id: GroupId
  label: string
  description: string
  attributes?: AttributeField[]
  /** False until the feature ships; shown as "coming soon". */
  available: boolean
}

export const FIELD_GROUPS: FieldGroup[] = [
  {
    id: 'brand',
    label: 'Brand & manufacturer',
    description: 'Filter and search products by brand.',
    available: true,
  },
  {
    id: 'pricing',
    label: 'MRP',
    description: 'The printed maximum retail price, with a warning when you sell above it.',
    available: true,
  },
  {
    id: 'packs',
    label: 'Pack sizes & extra barcodes',
    description: 'Scan a box barcode to sell 12 at once; more than one barcode per product.',
    available: true,
  },
  {
    id: 'shelf',
    label: 'Shelf & reorder',
    description: 'Rack/shelf location, tags, and how much to reorder.',
    available: true,
  },
  {
    id: 'trade',
    label: 'HS code',
    description: 'Harmonised System code for customs and tax reports.',
    available: true,
  },
  {
    id: 'batches',
    label: 'Batches & expiry',
    description: 'Batch number, manufacture and expiry dates; sell the earliest expiry first.',
    available: true,
  },
  {
    id: 'variants',
    label: 'Variants',
    description: 'Size, colour and other options, each with its own code, price and stock.',
    available: true,
  },
  {
    id: 'serials',
    label: 'Serial / IMEI numbers',
    description: 'Track each unit by serial or IMEI, with warranty.',
    available: true,
  },
  {
    id: 'wholesale',
    label: 'Wholesale & minimum prices',
    description: 'A wholesale price for wholesale customers, and a price staff can’t go below.',
    available: true,
  },
  {
    id: 'scale',
    label: 'Weighing-scale barcodes',
    description: 'Scan stickers from a label scale: item code plus weight or price.',
    available: true,
  },
  {
    id: 'pharmacy',
    label: 'Medicine details',
    description: 'Generic name, strength, dosage form and prescription.',
    available: true,
    attributes: [
      { key: 'generic_name', label: 'Generic name', type: 'text', placeholder: 'Paracetamol' },
      { key: 'strength', label: 'Strength', type: 'text', placeholder: '500 mg' },
      {
        key: 'dosage_form',
        label: 'Dosage form',
        type: 'select',
        options: [
          'Tablet',
          'Capsule',
          'Syrup',
          'Suspension',
          'Injection',
          'Ointment',
          'Drops',
          'Inhaler',
          'Other',
        ],
      },
      { key: 'prescription_required', label: 'Prescription required', type: 'yesno' },
    ],
  },
  {
    id: 'agro',
    label: 'Agro details',
    description:
      'Active ingredient, formulation, the crop or animal it is for, registration number.',
    available: true,
    attributes: [
      {
        key: 'active_ingredient',
        label: 'Active ingredient',
        type: 'text',
        placeholder: 'e.g. Chlorpyrifos 20% EC',
      },
      {
        key: 'formulation',
        label: 'Formulation',
        type: 'select',
        options: ['EC', 'WP', 'SC', 'Granules', 'Liquid', 'Powder', 'Other'],
      },
      {
        key: 'for_crop',
        label: 'For crop or animal',
        type: 'text',
        placeholder: 'e.g. Paddy, maize · or Cattle, poultry',
      },
      {
        key: 'registration_no',
        label: 'Registration no.',
        type: 'text',
        placeholder: 'As printed on the pack',
      },
    ],
  },
  {
    id: 'apparel',
    label: 'Clothing details',
    description: 'Material and season.',
    available: true,
    attributes: [
      { key: 'material', label: 'Material', type: 'text', placeholder: 'Cotton' },
      {
        key: 'season',
        label: 'Season',
        type: 'select',
        options: ['All season', 'Summer', 'Winter', 'Festival'],
      },
    ],
  },
  {
    id: 'electronics',
    label: 'Electronics details',
    description: 'Model number.',
    available: true,
    attributes: [{ key: 'model_no', label: 'Model no.', type: 'text' }],
  },
  {
    id: 'photo',
    label: 'Photos',
    description: 'A picture of the product. Restaurants see it on the menu when taking an order.',
    available: true,
  },
  {
    id: 'custom',
    label: 'Custom fields',
    description: 'Your own product fields, set up below.',
    available: true,
  },
]

export const GROUP_BY_ID = Object.fromEntries(FIELD_GROUPS.map((g) => [g.id, g])) as Record<
  GroupId,
  FieldGroup
>

export type BusinessType = TenantSettingsRow['business_type']

export const BUSINESS_TYPES: {
  id: BusinessType
  label: string
  example: string
  groups: GroupId[]
}[] = [
  {
    id: 'kirana',
    label: 'Kirana & grocery',
    example: 'Rice, noodles, oil, snacks',
    groups: ['brand', 'pricing', 'packs', 'shelf', 'wholesale'],
  },
  {
    id: 'pharmacy',
    label: 'Pharmacy',
    example: 'Medicines, surgical items',
    groups: ['brand', 'pricing', 'packs', 'shelf', 'batches', 'pharmacy'],
  },
  {
    id: 'hardware',
    label: 'Hardware & paints',
    example: 'Tools, pipes, cement, paint',
    groups: ['brand', 'packs', 'shelf', 'trade', 'wholesale'],
  },
  {
    id: 'apparel',
    label: 'Clothing & footwear',
    example: 'Shirts, sarees, shoes',
    groups: ['brand', 'photo', 'variants', 'apparel'],
  },
  {
    id: 'electronics',
    label: 'Mobile & electronics',
    example: 'Phones, chargers, appliances',
    groups: ['brand', 'pricing', 'serials', 'electronics'],
  },
  {
    id: 'cosmetics',
    label: 'Cosmetics & beauty',
    example: 'Creams, make-up, perfume',
    groups: ['brand', 'pricing', 'batches'],
  },
  {
    id: 'bakery',
    label: 'Bakery & sweets',
    example: 'Cakes, bread, mithai',
    groups: ['packs', 'batches', 'scale'],
  },
  {
    id: 'agrovet',
    label: 'Agrovet',
    example: 'Seeds, fertiliser, pesticides, feed',
    groups: ['brand', 'pricing', 'packs', 'shelf', 'batches', 'wholesale', 'agro'],
  },
  {
    id: 'restaurant',
    label: 'Restaurant & café',
    example: 'Tables, dishes, drinks',
    groups: ['brand', 'photo'],
  },
  {
    id: 'workshop',
    label: 'Workshop & repair',
    example: 'Spare parts, labour, servicing',
    groups: ['brand', 'shelf'],
  },
  {
    id: 'wholesale',
    label: 'Wholesale & distribution',
    example: 'Cartons, bulk goods',
    groups: ['brand', 'pricing', 'packs', 'trade', 'wholesale'],
  },
  {
    id: 'general',
    label: 'Other shop',
    example: 'Stationery, gifts, anything else',
    groups: ['brand', 'shelf'],
  },
]

/** A business is up to this many kinds; the first is the main one. */
export const MAX_KINDS = 3

/** "All in one" at sign-up: a shortcut that ticks three broad kinds, not a kind of its own. */
export const ALL_IN_ONE: BusinessType[] = ['kirana', 'hardware', 'cosmetics']

const isKind = (v: unknown): v is BusinessType =>
  typeof v === 'string' && BUSINESS_TYPES.some((t) => t.id === v)

/**
 * The business's kinds, the main one first, each once, at most three. A restaurant is one
 * kind only. The one place `business_kinds` is read.
 */
export function businessKinds(
  settings:
    | (Pick<TenantSettingsRow, 'business_type'> &
        Partial<Pick<TenantSettingsRow, 'business_kinds'>>)
    | null
    | undefined,
): BusinessType[] {
  const main = settings?.business_type ?? 'general'
  if (main === 'restaurant') return [main]
  const others = Array.isArray(settings?.business_kinds) ? settings.business_kinds : []
  const kinds: BusinessType[] = [main]
  for (const k of others) {
    if (isKind(k) && k !== 'restaurant' && !kinds.includes(k)) kinds.push(k)
  }
  return kinds.slice(0, MAX_KINDS)
}

/** Sells services as well as goods: the business's choice, else on for a workshop. */
export function providesServices(
  settings:
    | (Pick<TenantSettingsRow, 'business_type'> &
        Partial<Pick<TenantSettingsRow, 'provides_services'>>)
    | null
    | undefined,
): boolean {
  if (typeof settings?.provides_services === 'boolean') return settings.provides_services
  return settings?.business_type === 'workshop'
}

/** Grey examples for a product form's empty boxes, in the main kind's words. Never a value. */
export interface TypeHints {
  name: string
  category: string
  brand: string
}

export const TYPE_HINTS: Record<BusinessType, TypeHints> = {
  kirana: { name: 'e.g. Wai Wai noodles, 75 g', category: 'e.g. Noodles', brand: 'e.g. CG Foods' },
  pharmacy: {
    name: 'e.g. Cetamol 500 mg',
    category: 'e.g. Pain relief',
    brand: 'e.g. Nepal Pharma',
  },
  hardware: { name: 'e.g. PPR pipe 1 inch', category: 'e.g. Plumbing', brand: 'e.g. Panchakanya' },
  apparel: { name: 'e.g. Cotton kurta', category: 'e.g. Kurtas', brand: 'e.g. Sonam' },
  electronics: {
    name: 'e.g. 20W USB-C charger',
    category: 'e.g. Chargers',
    brand: 'e.g. Samsung',
  },
  cosmetics: {
    name: 'e.g. Aloe vera face wash',
    category: 'e.g. Skin care',
    brand: 'e.g. Himalaya',
  },
  bakery: {
    name: 'e.g. Black forest cake, 1 lb',
    category: 'e.g. Cakes',
    brand: 'e.g. Own bakery',
  },
  wholesale: {
    name: 'e.g. Sunflower oil, carton of 12',
    category: 'e.g. Oil',
    brand: 'e.g. Dhara',
  },
  restaurant: { name: 'e.g. Chicken momo', category: 'e.g. Momo', brand: 'e.g. Coca-Cola' },
  workshop: { name: 'e.g. Brake pad, front', category: 'e.g. Brakes', brand: 'e.g. Bosch' },
  agrovet: {
    name: 'e.g. Urea 46% N, 50 kg',
    category: 'e.g. Fertiliser',
    brand: 'e.g. Krishi Sewa',
  },
  general: {
    name: 'e.g. A4 notebook, 200 pages',
    category: 'e.g. Stationery',
    brand: 'e.g. Classmate',
  },
}

/** The main kind's examples for the empty boxes. */
export function typeHints(
  settings: Pick<TenantSettingsRow, 'business_type'> | null | undefined,
): TypeHints {
  return TYPE_HINTS[settings?.business_type ?? 'general'] ?? TYPE_HINTS.general
}

/** Categories a new business of the kind is offered on its empty Products page. */
export const STARTER_CATEGORIES: Partial<Record<BusinessType, string[]>> = {
  agrovet: [
    'Seeds',
    'Fertiliser',
    'Pesticides',
    'Animal feed',
    'Vet medicines',
    'Tools & sprayers',
  ],
  apparel: ['Kurtas', 'Shirts', 'T-shirts', 'Pants & jeans', 'Sarees', 'Kids', 'Footwear'],
}

/** The field groups of one kind, or of several together (in FIELD_GROUPS order). */
export function defaultGroups(
  type: BusinessType | readonly BusinessType[] | null | undefined,
): GroupId[] {
  const kinds = Array.isArray(type) ? type : [type]
  const wanted = new Set<GroupId>()
  for (const k of kinds) {
    for (const g of BUSINESS_TYPES.find((t) => t.id === k)?.groups ?? ['brand', 'shelf']) {
      wanted.add(g)
    }
  }
  return FIELD_GROUPS.map((g) => g.id).filter((g) => wanted.has(g))
}

const isGroupId = (v: unknown): v is GroupId => typeof v === 'string' && v in GROUP_BY_ID

/** The groups this business shows: its own choice, else its type's defaults. Unreleased groups are dropped. */
export function enabledGroups(
  settings:
    | (Pick<TenantSettingsRow, 'business_type' | 'field_groups'> &
        Partial<Pick<TenantSettingsRow, 'business_kinds'>>)
    | null
    | undefined,
): Set<GroupId> {
  const chosen = Array.isArray(settings?.field_groups)
    ? settings.field_groups.filter(isGroupId)
    : defaultGroups(businessKinds(settings))
  return new Set(chosen.filter((g) => GROUP_BY_ID[g].available))
}

/** Custom fields from settings, keeping only well-formed entries. */
export function customFields(
  settings: Pick<TenantSettingsRow, 'custom_fields'> | null | undefined,
): AttributeField[] {
  const raw = settings?.custom_fields
  if (!Array.isArray(raw)) return []
  const out: AttributeField[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const { key, label, type, options } = item as Record<string, JsonValue>
    if (typeof key !== 'string' || typeof label !== 'string') continue
    const t = (['text', 'number', 'date', 'yesno', 'select'] as const).find((x) => x === type)
    if (!t) continue
    out.push({
      key,
      label,
      type: t,
      options: Array.isArray(options)
        ? options.filter((o): o is string => typeof o === 'string')
        : undefined,
    })
  }
  return out
}

/** Attribute fields to show for the enabled groups (group fields, then custom fields). */
export function attributeFields(
  groups: ReadonlySet<GroupId>,
  custom: AttributeField[],
): { group: FieldGroup; fields: AttributeField[] }[] {
  const out: { group: FieldGroup; fields: AttributeField[] }[] = []
  for (const g of FIELD_GROUPS) {
    if (!groups.has(g.id)) continue
    if (g.id === 'custom') {
      if (custom.length) out.push({ group: g, fields: custom })
    } else if (g.attributes?.length) {
      out.push({ group: g, fields: g.attributes })
    }
  }
  return out
}

/** "Rack no." → "rack_no" (a stable key for a new custom field). */
export function fieldKey(label: string, taken: ReadonlySet<string>): string {
  const base =
    label
      .normalize('NFKD')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 30) || 'field'
  let key = base
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`
  return key
}

/** Attributes without empty values (so products don't store { strength: "" }). */
export function cleanAttributes(
  values: Record<string, JsonValue | undefined>,
): Record<string, JsonValue> | null {
  const out: Record<string, JsonValue> = {}
  for (const [k, v] of Object.entries(values)) {
    if (v === undefined || v === null || v === '') continue
    out[k] = v
  }
  return Object.keys(out).length ? out : null
}

/** Text shown for an attribute value. */
export function formatAttribute(field: AttributeField, value: JsonValue | undefined): string {
  if (value === undefined || value === null || value === '') return '—'
  if (field.type === 'yesno') return value ? 'Yes' : 'No'
  return String(value)
}

/** Tags as a clean list: "otc, fever , OTC" → ["otc", "fever"]. */
export function parseTags(text: string | null | undefined): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of (text ?? '').split(/[,;]/)) {
    const tag = raw.trim()
    if (!tag || seen.has(tag.toLowerCase())) continue
    seen.add(tag.toLowerCase())
    out.push(tag)
  }
  return out
}
